import { createRef } from "react";

import { shallowEqual } from "shallow-equal";

import { defaultAttributes } from "@Components/Component";

import { ComponentAjax, type IComponentAjax, type IComponentAjaxState } from "../ComponentAjax";
import { Loading } from "../Loading";
import { type IMapMarker, MapMarker } from "./MapMarker";
import { MapScripts } from "./MapScripts";

import * as css from "./Map.module.css";

export interface IMap extends IComponentAjax<IMapMarker[]> {
    googleMapsUrl: string;
    mapOptions?: google.maps.MapOptions;
    defaultPosition?: google.maps.LatLngLiteral;
    onGoogleMapsInitialized?: () => void;
    renderMarker?: (props: IMapMarker) => React.ReactElement<IMapMarker> | null;
    multipleMarkerInfo?: boolean;
    centerMarkersOnScreen?: boolean;
}

interface IMapState extends IComponentAjaxState {
    ajaxLoading: boolean;
    markers: IMapMarker[];
    initialized: boolean;
}

export * from "./MapMarker";

declare global {
    // tslint:disable-next-line:interface-name
    interface Window {
        __mapinstance: google.maps.Map;
    }
}

export class Map extends ComponentAjax<IMapMarker[], IMap, IMapState> {
    public static readonly DefaultMapOptions: google.maps.MapOptions = {
        center: {
            lat: 52.22977,
            lng: 21.01178,
        },
        zoom: 10,
        mapTypeControl: true,
        mapTypeControlOptions: {
            style: 0,
            mapTypeIds: ["satellite", "roadmap", "hybrid", "terrain"],
        },
    };

    public static defaultProps: IMap = {
        googleMapsUrl: "",
        mapOptions: Map.DefaultMapOptions,
        renderMarker: (props: IMapMarker) => <MapMarker {...props} />,
        multipleMarkerInfo: false,
        centerMarkersOnScreen: true,
    };

    private static mapElement: HTMLElement | undefined;

    private mapContainer = createRef<HTMLDivElement>();

    private initialized: () => void;

    constructor(props: IMap) {
        super(props);

        this.state = {
            isLoading: MapScripts.GoogleScriptsLoaded === false,
            ajaxLoading: false,
            markers: this.props.initialData || [],
            initialized: false,
        };

        this.initialized = () => {
            const onInitialized = () => {
                if (this.props.onGoogleMapsInitialized) {
                    this.props.onGoogleMapsInitialized();
                }

                this.ajaxRequest();
            };

            this.showMap(onInitialized);

            this.setState({
                isLoading: false,
                initialized: true,
                ajaxLoading: !!this.props.ajaxAction,
            });
        };
    }

    private static get mapInstance() {
        return window.__mapinstance;
    }

    private static set mapInstance(mapInstance: google.maps.Map) {
        window.__mapinstance = mapInstance;
    }

    public componentDidMount() {
        if (!Map.mapElement) {
            Map.mapElement = document.createElement("div");
            Map.mapElement.classList.add(css.mapInstance);
        }

        this.loadGoogleScripts();
    }

    public componentDidUpdate(prevProps: IMap) {
        if (!Map.mapInstance) {
            return;
        }

        if (shallowEqual(prevProps.mapOptions, this.props.mapOptions) === false && this.props.mapOptions) {
            Map.mapInstance.setOptions(this.props.mapOptions);
        }
    }

    public componentWillUnmount() {
        this.initialized = () => undefined;
    }

    public render() {
        return (
            <div {...defaultAttributes(this.props, css.mapComponent)}>
                {this.renderLoading()}
                {this.state.ajaxLoading && <Loading isModal={true} />}
                <div className={css.mapContainer} ref={this.mapContainer} />
                {this.renderMarkers()}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: IMapMarker[]) {
        this.setState((prevState): IMapState => {
            if (!response) {
                return { ...prevState, ajaxLoading: false, markers: prevState.markers };
            }

            return { ...prevState, ajaxLoading: false, markers: response };
        });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    protected showMap(initialized: () => void) {
        const initializeMap = (defaultPos: google.maps.LatLngLiteral) => {
            const mapOptions = this.props.mapOptions ? this.props.mapOptions : Map.DefaultMapOptions;

            mapOptions.center = defaultPos;

            const container = this.mapContainer.current;

            if (!container) {
                return;
            }

            if (!Map.mapInstance && Map.mapElement) {
                container.appendChild(Map.mapElement);
                Map.mapInstance = new google.maps.Map(Map.mapElement, mapOptions);
            } else {
                container.appendChild(Map.mapInstance.getDiv());
                Map.mapInstance.setOptions(mapOptions);
            }

            initialized();
        };

        if (this.props.defaultPosition) {
            initializeMap(this.props.defaultPosition);
            return;
        }

        const setLocation = (pos: GeolocationPosition) => {
            initializeMap({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        };

        const errorLocation = () => {
            initializeMap(Map.DefaultMapOptions.center as google.maps.LatLngLiteral);
        };

        navigator.geolocation.getCurrentPosition(setLocation, errorLocation);
    }

    private loadGoogleScripts() {
        MapScripts.EnsureScripts(
            this.props.googleMapsUrl,
            () => this.initialized(),
            () => {
                this.setState({ isLoading: false });
            },
        );
    }

    private renderMarkers() {
        if (this.state.initialized === false || !this.props.renderMarker) {
            return null;
        }

        const mapBounds = new google.maps.LatLngBounds();

        const fitBounds = (marker: google.maps.Marker) => {
            if (this.props.centerMarkersOnScreen === false) {
                return;
            }

            const position = marker.getPosition();
            if (!position) {
                return;
            }

            mapBounds.extend(position);
            Map.mapInstance.fitBounds(mapBounds);
        };

        return this.state.markers.map((marker, i) => {
            if (!this.props.renderMarker) {
                return null;
            }

            marker.key = i;
            marker.map = Map.mapInstance;
            marker.onLoad = fitBounds;
            marker.shouldAnimate = this.state.markers.length <= 10;
            marker.onOpenInfo = () => {
                const markers = [...this.state.markers];

                if (this.props.multipleMarkerInfo === false) {
                    const { length } = markers;
                    for (let pos = 0; pos < length; pos += 1) {
                        markers[pos].showInfo = false;
                    }
                }

                markers[i].showInfo = true;
                this.setState({ markers });
            };
            marker.onCloseInfo = () => {
                const markers = this.state.markers.slice();
                markers[i].showInfo = false;
                this.setState({ markers });
            };

            return this.props.renderMarker(marker);
        });
    }
}
